import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useTranslation } from "@i18n";
import Input from "@/components/UiKit/Input/Input";
import { useEffect, useState, useRef, useMemo } from "react";
import { Controller, useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Select from "@/components/UiKit/Select/Select";
import { api__getCitiesDropDown } from "@/apis/assets/api__get_cities_drop_down";
import { CDN_LINK } from "@globalConfig";
import ChangeAccountVerificationModal from "@/_MiniApps/auth/ui/ChangeAccountVerificationModal/ChangeAccountVerificationModal";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import useApi from "@hooks/useApi";
import Loader from "@componentsShared/Loader/Loader";
import FLowLoadingSpinner from "@/pages/sell-my-car/components/FLowLoadingSpinner";
import InputPhoneNumber from "@componentsShared/InputPhoneNumber/InputPhoneNumber";
import RHFCheckbox from "@/common/HOCs/rhf/checkbox/RHFCheckbox";
import { OrderPaidError, PostReservedError } from "@/apis/tamara/tamara";
import { AmwalValidationError, OrderPaidError as AmwalOrderPaidError, PostReservedError as AmwalPostReservedError } from "@/pages/checkout/bnpl/amwal/api/amwalApi";
import { toast } from "sonner";
import { trackEvents } from "@/common/events/events";
import { serverUrl } from "@globalConfig";
import useHelpers from "@hooks/useHelpers";
import { ValidationException } from "@/common/exceptions/ValidationException";
import useIsMobile from "@hooks/useIsMobile";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import { useAmwalFinance } from "@/common/Contexts/AmwalFinanceContext";
import { api__createAmwalCheckoutSession } from "@/pages/checkout/bnpl/amwal/api/amwalApi";
import { Icon } from "@syarahtech/ui";
import { useLang } from "@/common/Contexts/LanguageContext";
// Zod Validation Schema
var AmwalFormSchema = z.object({
  first_name: z.string().min(1, "tamara/first_name_required").min(2, "tamara/first_name_min_length").max(50, "tamara/first_name_max_length").regex(/^[\u0600-\u06FFa-zA-Z\s]+$/, "tamara/first_name_invalid_chars"),
  family_name: z.string().min(1, "tamara/family_name_required").min(2, "tamara/family_name_min_length").max(50, "tamara/family_name_max_length").regex(/^[\u0600-\u06FFa-zA-Z\s]+$/, "tamara/family_name_invalid_chars"),
  mobile_number: z.string().min(1, "tamara/mobile_required").regex(/^05\d{8}$/, "tamara/mobile_invalid_format"),
  city_id: z.string().min(1, "tamara/city_required"),
  whatsapp_optin: z.coerce.number()["default"](1)
});
// Helper function to safely extract names from user object
var extractUserNames = function extractUserNames(user) {
  if (!(user !== null && user !== void 0 && user.full_name)) return {
    first_name: "",
    family_name: ""
  };
  var nameParts = user.full_name.trim().split(" ");
  var first_name = nameParts[0] || "";
  var family_name = nameParts.slice(1).join(" ") || "";
  return {
    first_name: first_name,
    family_name: family_name
  };
};
var AmwalInfoComponent = function AmwalInfoComponent(_ref) {
  var setIsOtpStep = _ref.setIsOtpStep,
    autosend = _ref.autosend,
    onFormUpdated = _ref.onFormUpdated,
    onFormCompleted = _ref.onFormCompleted;
  var _useTranslation = useTranslation(["web_checkout_finance"]),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var _useSession = useSession(),
    user = _useSession.user;
  var _useAuthActions = useAuthActions(),
    logout = _useAuthActions.logout;
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var _useAmwalFinance = useAmwalFinance(),
    amwalActions = _useAmwalFinance.actions,
    amwalState = _useAmwalFinance.state;
  var checkoutOrder = amwalState.checkoutOrder,
    contextUserInfo = amwalState.userInfo;
  var fetch__getOrderDetailsByParams = useAmwalFinance().remote_actions.fetch__getOrderDetailsByParams;
  var isMobile = useIsMobile();
  var lang = useLang();
  var _useState = useState(false),
    showVerificationModal = _useState[0],
    setShowVerificationModal = _useState[1];
  var _useApi = useApi([], api__getCitiesDropDown),
    _useApi2 = _slicedToArray(_useApi, 3),
    loadingCities = _useApi2[0],
    error = _useApi2[1],
    cities = _useApi2[2];
  var _useState2 = useState(false),
    isSubmitting = _useState2[0],
    setIsSubmitting = _useState2[1];
  var post_id = useAmwalFinance().queryParams.post_id;

  // Extract user names from session
  var userNames = extractUserNames(user);

  // Initialize default values from context/session/localStorage
  var defaultValues = useMemo(function () {
    var _contextUserInfo$what;
    return {
      first_name: (contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.first_name) || userNames.first_name || "",
      family_name: (contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.family_name) || userNames.family_name || "",
      mobile_number: (user === null || user === void 0 ? void 0 : user.phone_number) || (contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.mobile_number) || "",
      city_id: String((contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.city_id) || "1"),
      whatsapp_optin: Number((_contextUserInfo$what = contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.whatsapp_optin) !== null && _contextUserInfo$what !== void 0 ? _contextUserInfo$what : 1)
    };
  }, [contextUserInfo, user, userNames]);

  // Initialize RHF
  var methods = useForm({
    resolver: zodResolver(AmwalFormSchema),
    mode: "onTouched",
    defaultValues: defaultValues
  });
  var control = methods.control,
    handleSubmit = methods.handleSubmit,
    errors = methods.formState.errors,
    watch = methods.watch,
    setValue = methods.setValue,
    setError = methods.setError,
    trigger = methods.trigger;

  // Watch specific form values
  var firstNameValue = watch("first_name");
  var familyNameValue = watch("family_name");
  var mobileNumberValue = watch("mobile_number");
  var cityIdValue = watch("city_id");
  var whatsappOptinValue = watch("whatsapp_optin");

  // Track previous city ID for change detection
  var previousCityId = useRef(cityIdValue);

  // Track previous values to avoid unnecessary updates
  var previousValues = useRef({
    first_name: firstNameValue,
    family_name: familyNameValue,
    mobile_number: mobileNumberValue,
    city_id: cityIdValue,
    whatsapp_optin: whatsappOptinValue
  });

  // Sync with localStorage only when values actually change (debounced to prevent loops)
  useEffect(function () {
    var prev = previousValues.current;
    var hasChanged = prev.first_name !== firstNameValue || prev.family_name !== familyNameValue || prev.mobile_number !== mobileNumberValue || prev.city_id !== cityIdValue || prev.whatsapp_optin !== whatsappOptinValue;
    if (hasChanged && amwalActions !== null && amwalActions !== void 0 && amwalActions.action__updateUserInfo) {
      var timer = setTimeout(function () {
        // Find city name from cities array
        var selectedCity = cities.find(function (c) {
          return String(c.id) === String(cityIdValue);
        });
        amwalActions.action__updateUserInfo({
          first_name: firstNameValue || null,
          family_name: familyNameValue || null,
          mobile_number: mobileNumberValue || null,
          city_id: cityIdValue || null,
          city_name: (selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_english) || null,
          whatsapp_optin: whatsappOptinValue
        });
        // Update previous values after syncing
        previousValues.current = {
          first_name: firstNameValue,
          family_name: familyNameValue,
          mobile_number: mobileNumberValue,
          city_id: cityIdValue,
          whatsapp_optin: whatsappOptinValue
        };
      }, 300);
      return function () {
        return clearTimeout(timer);
      };
    }
  }, [firstNameValue, familyNameValue, mobileNumberValue, cityIdValue, whatsappOptinValue, amwalActions, cities]);

  // Sync with context when localStorage loads
  useEffect(function () {
    if (contextUserInfo) {
      var _contextUserInfo$what2;
      setValue("first_name", contextUserInfo.first_name || "");
      setValue("family_name", contextUserInfo.family_name || "");
      setValue("mobile_number", contextUserInfo.mobile_number || "");
      setValue("city_id", String(contextUserInfo.city_id || "1"));
      setValue("whatsapp_optin", Number((_contextUserInfo$what2 = contextUserInfo.whatsapp_optin) !== null && _contextUserInfo$what2 !== void 0 ? _contextUserInfo$what2 : 1));
    }
  }, [contextUserInfo, setValue]);

  // City change handler - refresh checkout order
  useEffect(function () {
    if (cityIdValue && previousCityId.current !== undefined && previousCityId.current !== cityIdValue && String(contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.city_id) !== String(cityIdValue)) {
      var _checkoutOrder$warran, _checkoutOrder$warran2;
      // Preserve the currently selected warranty across the city re-fetch
      var currentWarrantyId = checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$warran = checkoutOrder.warrantyV2) === null || _checkoutOrder$warran === void 0 ? void 0 : (_checkoutOrder$warran2 = _checkoutOrder$warran.selected) === null || _checkoutOrder$warran2 === void 0 ? void 0 : _checkoutOrder$warran2.id;
      fetch__getOrderDetailsByParams(_objectSpread({
        post_id: post_id,
        city_id: String(cityIdValue)
      }, currentWarrantyId != null && {
        warranty_id: currentWarrantyId
      })).then(function (res) {
        if (res) {
          var msg = t("shipping_cost_updated", {
            ns: "web_checkout_cash"
          });
          toast.success(msg, {
            position: "bottom-center",
            duration: 2000,
            style: {
              zIndex: 9999999
            }
          });
        }
      });
    }
    previousCityId.current = cityIdValue;
  }, [cityIdValue, post_id, contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.city_id]);

  // Auto-send functionality - call onSubmit directly (data was already validated before OTP)
  useEffect(function () {
    if (user !== null && user !== void 0 && user.id && autosend && !isSubmitting) {
      var hasCompleteUserInfo = firstNameValue && familyNameValue && mobileNumberValue && cityIdValue;
      if (hasCompleteUserInfo) {
        onSubmit({
          first_name: firstNameValue,
          family_name: familyNameValue,
          mobile_number: mobileNumberValue,
          city_id: cityIdValue,
          whatsapp_optin: whatsappOptinValue
        });
      }
    }
  }, [autosend]);
  var onSubmit = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(data) {
      var selectedCity, _checkoutOrder$warran3, _checkoutOrder$warran4, _response$data, searchParams, amwalRequest, response, _response$data2, _response$data3, _checkoutOrder$g4_dat, _checkoutOrder$g4_dat2, _checkoutOrder$g4_dat3, _checkoutOrder$g4_dat4, _checkoutOrder$g4_dat5, _checkoutOrder$g4_dat6, _checkoutOrder$g4_dat7, _error$response, apiToFormFieldMap, _errorData$status, _errorData$status2, _errorData$status3, errorData, firstError, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            // Find city name from cities array
            selectedCity = cities.find(function (c) {
              return String(c.id) === String(data.city_id);
            }); // Force update context/local storage with latest form data
            // This ensures that even if the component unmounts immediately (e.g. switching to OTP step),
            // the latest data is preserved in context and local storage.
            amwalActions === null || amwalActions === void 0 ? void 0 : amwalActions.action__updateUserInfo({
              first_name: data.first_name,
              family_name: data.family_name,
              mobile_number: data.mobile_number,
              city_id: data.city_id,
              city_name: (selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_english) || null,
              whatsapp_optin: data.whatsapp_optin
            });

            // Check if user is logged in first - if not, show OTP step
            if (user !== null && user !== void 0 && user.id) {
              _context.n = 1;
              break;
            }
            onFormCompleted === null || onFormCompleted === void 0 ? void 0 : onFormCompleted();
            setIsOtpStep(true);
            return _context.a(2);
          case 1:
            if (!(!autosend && (data === null || data === void 0 ? void 0 : data.mobile_number) != null && data !== null && data !== void 0 && data.mobile_number && data.mobile_number != (user === null || user === void 0 ? void 0 : user.phone_number) && !showVerificationModal)) {
              _context.n = 2;
              break;
            }
            return _context.a(2, setShowVerificationModal(true));
          case 2:
            if (post_id) {
              _context.n = 3;
              break;
            }
            toast.error(tShort("tamara/car_id_missing"));
            return _context.a(2);
          case 3:
            setIsSubmitting(true);
            _context.p = 4;
            searchParams = new URLSearchParams(__IS_CSR__ ? window.location.search : ""); // Prepare Amwal checkout request
            amwalRequest = _objectSpread({
              post_id: post_id,
              first_name: data.first_name.trim(),
              last_name: data.family_name.trim(),
              phone_number: data.mobile_number,
              city_id: parseInt(data.city_id),
              whatsapp_optin: data.whatsapp_optin,
              success_url: "".concat(serverUrl, "/").concat(lang == "en" ? "en/" : "", "finance-apply-amwal/payment-status?post_id=").concat(post_id),
              failure_url: "".concat(serverUrl, "/").concat(lang == "en" ? "en/" : "", "finance-apply-amwal/payment-status?post_id=").concat(post_id),
              cancel_url: "".concat(serverUrl, "/").concat(lang == "en" ? "en/" : "", "finance-apply-amwal/payment-status?post_id=").concat(post_id)
            }, (checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$warran3 = checkoutOrder.warrantyV2) === null || _checkoutOrder$warran3 === void 0 ? void 0 : (_checkoutOrder$warran4 = _checkoutOrder$warran3.selected) === null || _checkoutOrder$warran4 === void 0 ? void 0 : _checkoutOrder$warran4.id) != null && {
              v_warranty_id: checkoutOrder.warrantyV2.selected.id
            });
            _context.n = 5;
            return api__createAmwalCheckoutSession(amwalRequest);
          case 5:
            response = _context.v;
            if (response.success && (_response$data = response.data) !== null && _response$data !== void 0 && _response$data.checkout_url) {
              amwalActions === null || amwalActions === void 0 ? void 0 : amwalActions.action__updateUserInfo({
                first_name: data.first_name,
                family_name: data.family_name,
                mobile_number: data.mobile_number,
                city_id: data.city_id,
                city_name: (selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_english) || null,
                whatsapp_optin: data.whatsapp_optin,
                order_id: String(((_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.order_id) || "")
              });
              trackEvents("amwal_form_submit", {
                post_id: post_id,
                order_id: String(((_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : _response$data3.order_id) || ""),
                user_city: (selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_english) || String(data.city_id),
                inventory_type: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat === void 0 ? void 0 : _checkoutOrder$g4_dat.inventory_type,
                post_make: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat2 = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat2 === void 0 ? void 0 : _checkoutOrder$g4_dat2.post_make,
                post_model: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat3 = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat3 === void 0 ? void 0 : _checkoutOrder$g4_dat3.post_model,
                post_trim: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat4 = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat4 === void 0 ? void 0 : _checkoutOrder$g4_dat4.post_ext,
                post_year: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat5 = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat5 === void 0 ? void 0 : _checkoutOrder$g4_dat5.post_year,
                post_condition: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat6 = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat6 === void 0 ? void 0 : _checkoutOrder$g4_dat6.post_condition,
                post_price: checkoutOrder === null || checkoutOrder === void 0 ? void 0 : (_checkoutOrder$g4_dat7 = checkoutOrder.g4_data_layer) === null || _checkoutOrder$g4_dat7 === void 0 ? void 0 : _checkoutOrder$g4_dat7.post_price,
                total_amount: checkoutOrder !== null && checkoutOrder !== void 0 && checkoutOrder.total_price ? parseFloat(checkoutOrder.total_price.replace(/,/g, "")) : undefined,
                platform: getPlatformInfo(),
                referral_location: "pre-checkout"
              });
              onFormCompleted === null || onFormCompleted === void 0 ? void 0 : onFormCompleted();
              window.location.href = response.data.checkout_url;
            } else {
              toast.error(tShort("tamara/payment_session_error"));
            }
            _context.n = 11;
            break;
          case 6:
            _context.p = 6;
            _t = _context.v;
            console.error("Error creating Amwal checkout:", _t);

            // Handle Amwal rejection errors - redirect based on rejection reason

            // Handle reserve and paid errors first (these take priority)
            if (!(_t instanceof PostReservedError || _t instanceof AmwalPostReservedError)) {
              _context.n = 7;
              break;
            }
            toast.error(t("car_currently_reserved", {
              ns: "web_checkout_cash"
            }));
            // Redirect to car detail page after a brief delay
            setTimeout(function () {
              navigateTo("/cardetail/".concat(post_id));
            }, 2000);
            return _context.a(2);
          case 7:
            if (!(_t instanceof OrderPaidError || _t instanceof AmwalOrderPaidError)) {
              _context.n = 8;
              break;
            }
            toast.error(t("order_prepaid", {
              ns: "web_checkout_cash"
            }));
            // Redirect to tracking page after a brief delay
            setTimeout(function () {
              navigateTo("/tracking");
            }, 2000);
            return _context.a(2);
          case 8:
            if (!(_t instanceof ValidationException)) {
              _context.n = 9;
              break;
            }
            toast.error(_t.message);
            return _context.a(2);
          case 9:
            if (!(_t instanceof AmwalValidationError)) {
              _context.n = 10;
              break;
            }
            apiToFormFieldMap = {
              first_name: "first_name",
              last_name: "family_name",
              phone_number: "mobile_number",
              city_id: "city_id",
              whatsapp_optin: "whatsapp_optin"
            };
            _t.validationErrors.forEach(function (errorObj) {
              Object.entries(errorObj).forEach(function (_ref3) {
                var _ref4 = _slicedToArray(_ref3, 2),
                  key = _ref4[0],
                  message = _ref4[1];
                var formField = apiToFormFieldMap[key];
                if (formField) {
                  setError(formField, {
                    type: "server",
                    message: message
                  });
                } else {
                  toast.error(message, {
                    position: 'top-center',
                    dismissible: false
                  });
                }
              });
            });
            return _context.a(2);
          case 10:
            // Handle other specific error responses
            if (_t !== null && _t !== void 0 && (_error$response = _t.response) !== null && _error$response !== void 0 && _error$response.data) {
              errorData = _t.response.data;
              if (((_errorData$status = errorData.status) === null || _errorData$status === void 0 ? void 0 : _errorData$status.code) === 1004) {
                toast.error(tShort("tamara/car_not_eligible"), {
                  position: 'top-center',
                  dismissible: false
                });
              } else if (((_errorData$status2 = errorData.status) === null || _errorData$status2 === void 0 ? void 0 : _errorData$status2.code) === 401) {
                toast.error(tShort("tamara/please_login"), {
                  position: 'top-center',
                  dismissible: false
                });
              } else if (errorData.errors) {
                firstError = Object.values(errorData.errors)[0];
                if (typeof firstError === "string") {
                  toast.error(firstError, {
                    position: 'top-center',
                    dismissible: false
                  });
                } else {
                  toast.error(tShort("tamara/check_data"), {
                    position: 'top-center',
                    dismissible: false
                  });
                }
              } else if ((_errorData$status3 = errorData.status) !== null && _errorData$status3 !== void 0 && _errorData$status3.message) {
                toast.error(errorData.status.message, {
                  position: 'top-center',
                  dismissible: false
                });
              } else {
                toast.error(tShort("tamara/unexpected_error"), {
                  position: 'top-center',
                  dismissible: false
                });
              }
            } else {
              toast.error(tShort("tamara/connection_error"), {
                position: 'top-center',
                dismissible: false
              });
            }
          case 11:
            _context.p = 11;
            setIsSubmitting(false);
            return _context.f(11);
          case 12:
            return _context.a(2);
        }
      }, _callee, null, [[4, 6, 11, 12]]);
    }));
    return function onSubmit(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  if (loadingCities) return /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center min-h-[600px]"
  }, /*#__PURE__*/React.createElement(FLowLoadingSpinner, null));
  return /*#__PURE__*/React.createElement(FormProvider, methods, /*#__PURE__*/React.createElement("div", {
    id: "amwal-form-container",
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    id: "amwal-form-inner",
    className: "".concat(isMobile ? "" : "")
  }, /*#__PURE__*/React.createElement("h1", {
    id: "amwal-form-title",
    className: "text-lg pb-5 pt-2 md:py-5 md-py-6 md:text-xl font-bold"
  }, tShort("tamara/form_title")), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "first_name",
    control: control,
    render: function render(_ref5) {
      var field = _ref5.field,
        error = _ref5.fieldState.error;
      return /*#__PURE__*/React.createElement(Input, _extends({}, field, {
        id: "checkout-firstname-input",
        name: "first_name",
        label: t("first_name", {
          ns: "web_checkout_finance"
        }),
        type: "text",
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : "",
        value: field.value,
        onChange: function onChange(event) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          field.onChange(event);
        }
      }));
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "family_name",
    control: control,
    render: function render(_ref6) {
      var field = _ref6.field,
        error = _ref6.fieldState.error;
      return /*#__PURE__*/React.createElement(Input, _extends({}, field, {
        id: "checkout-lastname-input",
        name: "family_name",
        label: t("last_name", {
          ns: "web_checkout_finance"
        }),
        type: "text",
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : "",
        value: field.value,
        onChange: function onChange(event) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          field.onChange(event);
        }
      }));
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "mobile_number",
    control: control,
    render: function render(_ref7) {
      var _ref7$field = _ref7.field,
        _onChange = _ref7$field.onChange,
        value = _ref7$field.value,
        onBlur = _ref7$field.onBlur,
        error = _ref7.fieldState.error;
      return /*#__PURE__*/React.createElement(InputPhoneNumber, {
        id: "checkout-phone-input",
        defaultValue: value,
        onChange: function onChange(nextValue) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          _onChange(nextValue);
        },
        onBlur: onBlur,
        errorLabel: error ? tShort(error.message, {
          ns: "validations"
        }) : "",
        direction: lang == "ar" ? "rtl" : "ltr"
      });
    }
  })), /*#__PURE__*/React.createElement("label", {
    id: "amwal-whatsapp-label",
    className: "flex items-center mt-5 mb-[34px] cursor-pointer [&>*]:mx-[3px] [&>img]:w-6 [&>span]:text-[#6c7a8d] [&>span]:text-xs [&>span]:leading-6"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: function onClick() {
      return onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
    }
  }, /*#__PURE__*/React.createElement(RHFCheckbox, {
    name: "whatsapp_optin",
    defaultChecked: true
  })), /*#__PURE__*/React.createElement("img", {
    id: "amwal-whatsapp-icon",
    src: CDN_LINK + "/assets/images/wpImg.svg",
    alt: "whts",
    width: "24px",
    height: "24px"
  }), /*#__PURE__*/React.createElement("span", {
    id: "amwal-whatsapp-text"
  }, t("like_receive_updates_order_via", {
    ns: "web_checkout_finance"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "city_id",
    control: control,
    render: function render(_ref8) {
      var field = _ref8.field,
        error = _ref8.fieldState.error;
      return /*#__PURE__*/React.createElement(Select, {
        id: "checkout-city-select",
        name: "city_id",
        topLabel: t("city", {
          ns: "web_checkout_finance"
        }),
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : "",
        onChange: function onChange(e) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          field.onChange(e.target.value);
        },
        value: field.value
      }, cities.map(function (city, idx) {
        return idx === 0 ? /*#__PURE__*/React.createElement("option", {
          disabled: true,
          selected: true,
          hidden: true,
          key: idx,
          value: ""
        }, t("select", {
          ns: "web_checkout_finance"
        })) : /*#__PURE__*/React.createElement("option", {
          key: city.id,
          value: city.id
        }, city.name);
      }));
    }
  }))), isMobile ? /*#__PURE__*/React.createElement("div", {
    id: "amwal-mobile-submit-wrapper",
    className: "fixed bottom-0 left-0 z-[1] flex justify-between items-center w-full bg-white px-4 pt-5 pb-7 text-lg "
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex justify-end w-full"
  }, /*#__PURE__*/React.createElement("button", {
    id: "checkout-submit-btn",
    type: "button",
    className: "w-full p-3 bg-primary-old text-white rounded text-center font-bold border-none outline-none text-base cursor-pointer hover:bg-primary-120 transition-colors duration-200 ease-in disabled:bg-[#E8EFF6] disabled:text-[#92A5C1] disabled:cursor-not-allowed disabled:pointer-events-none relative flex justify-between items-center px-3",
    onClick: handleSubmit(onSubmit),
    disabled: isSubmitting
  }, /*#__PURE__*/React.createElement("strong", null, tShort("amwal/continue_with_amwal")), lang == "ar" ? /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-left-alt-rounded",
    width: 6,
    height: 6
  }) : /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-right-alt-rounded",
    width: 6,
    height: 6
  }), isSubmitting && /*#__PURE__*/React.createElement(Loader, {
    className: "!absolute !inset-0 !w-auto !h-auto !bg-transparent [&>span]:!w-5 [&>span]:!h-5 [&>span]:!border-2 [&>span]:after:!w-5 [&>span]:after:!h-5 [&>span]:after:!border-2"
  })))) :
  /*#__PURE__*/
  /* Submit Button - Desktop */
  React.createElement("div", {
    id: "amwal-desktop-submit-wrapper",
    className: "mt-8 flex justify-end"
  }, /*#__PURE__*/React.createElement("button", {
    id: "checkout-submit-btn",
    type: "button",
    className: "button p-3 bg-primary-old text-white rounded text-center font-bold border-none outline-none text-base cursor-pointer disabled:bg-[#E8EFF6] disabled:text-[#92A5C1] disabled:cursor-not-allowed disabled:pointer-events-none relative flex w-[318px] justify-between items-center px-3",
    onClick: handleSubmit(onSubmit),
    disabled: isSubmitting
  }, /*#__PURE__*/React.createElement("strong", {
    className: "text-base font-bold leading-6"
  }, tShort("amwal/continue_with_amwal")), lang == "ar" ? /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-left-alt-rounded",
    width: 6,
    height: 6
  }) : /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-right-alt-rounded",
    width: 6,
    height: 6
  }), isSubmitting && /*#__PURE__*/React.createElement(Loader, {
    className: "!absolute !inset-0 !w-auto !h-auto !bg-transparent [&>span]:!w-5 [&>span]:!h-5 [&>span]:!border-2 [&>span]:after:!w-5 [&>span]:after:!h-5 [&>span]:after:!border-2"
  }))), /*#__PURE__*/React.createElement(ChangeAccountVerificationModal, {
    show: showVerificationModal,
    onClose: function onClose() {
      return setShowVerificationModal(false);
    },
    onSubmit: function onSubmit() {
      setShowVerificationModal(false);
      logout(null).then(function () {
        return setIsOtpStep(true);
      });
    },
    newPhoneNumber: mobileNumberValue,
    loading: false
  })));
};
export default AmwalInfoComponent;